import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { Accordion, AccordionItem } from '@heroui/react';
import { useLanguage } from '../../stores/language-store';
import { useSidebar } from './sidebar-provider';
import { DESIGN_TOKENS } from '../../constants';

interface NavItem {
  title: string;
  url: string;
  icon: string;
  items?: {
    title: string;
    url: string;
    icon: string;
  }[];
}

interface NavMainProps {
  items: NavItem[];
  groupLabel?: string;
}



export const NavMain: React.FC<NavMainProps> = ({ items, groupLabel }) => {
  const location = useLocation();
  const { isRTL } = useLanguage();
  const { isMini, isMobile, closeSidebar, toggleMiniSidebar } = useSidebar();
  const [expandedKeys, setExpandedKeys] = React.useState<Set<string>>(new Set());

  // Close sidebar on mobile when a link is clicked
  const handleLinkClick = (url: string) => {
    if (isMobile) {
      closeSidebar();
    }
    // Toggle mini sidebar when clicking on conversations
    if (url === '/chat/conversations') {
      toggleMiniSidebar();
    }
  };

  // Check if a path is active
  const isActive = (path: string, exact: boolean = true) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Auto-expand accordion based on current path
  React.useEffect(() => {
    const path = location.pathname;
    // console.log('📍 Path Changed - Auto Expand Check:', { path });
    
    items.forEach((item, index) => {
      if (item.items) {
        const hasActiveChild = item.items.some(subItem => 
          location.pathname === subItem.url || location.pathname.startsWith(subItem.url + '/')
        );
        
        // console.log('🔍 Checking item:', {
        //   itemTitle: item.title,
        //   itemIndex: index,
        //   hasActiveChild,
        //   key: `item-${index}`
        // });
        
        if (hasActiveChild) {
          setExpandedKeys(prev => {
            const newSet = new Set(prev);
            newSet.add(`item-${index}`);
            // console.log('✅ Auto-expanding:', {
            //   itemTitle: item.title,
            //   key: `item-${index}`,
            //   expandedKeys: Array.from(newSet)
            // });
            return newSet;
          });
        }
      }
    });
  }, [location.pathname, items]);

  return (
    <div className="space-y-1">
      {groupLabel && !isMini && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.2 }}
          className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide"
        >
          {groupLabel}
        </motion.p>
      )}
      
      {items.map((item, index) => {
        const hasChildren = item.items && item.items.length > 0;
        const itemIsActive = isActive(item.url, false);
        
        if (!hasChildren) {
          return (
            <Link
              key={item.title}
              to={item.url}
              onClick={() => handleLinkClick(item.url)}
              className={`flex items-center gap-3 py-2.5 ${DESIGN_TOKENS.navItem} transition-all-fast relative
                ${itemIsActive
                  ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }
                ${isMini ? 'justify-center px-3' : 'px-4'}
              `}
            >
              <Icon icon={item.icon} width={20} className="shrink-0" />
              {!isMini && (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.2 }}
                >
                  {item.title}
                </motion.span>
              )}
              {itemIsActive && (
                <motion.div
                  layoutId="sidebar-active-indicator"
                  className={`absolute ${isRTL ? 'right-0 rounded-l-md' : 'left-0 rounded-r-md'} w-1 h-8 bg-primary-600 dark:bg-primary-400`}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </Link>
          );
        }

        if (isMini) {
          // In mini mode, show parent as single item
          const hasActiveChild = item.items?.some(subItem => 
            isActive(subItem.url, false)
          );
          
          return (
            <Link
              key={item.title}
              to={item.items[0].url}
              onClick={() => handleLinkClick(item.items[0].url)}
              className={`flex items-center justify-center gap-3 py-2.5 px-3 ${DESIGN_TOKENS.navItem} transition-all-fast relative
                ${hasActiveChild
                  ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }
              `}
            >
              <Icon icon={item.icon} width={20} className="shrink-0" />
              {hasActiveChild && (
                <motion.div
                  layoutId="sidebar-active-indicator"
                  className={`absolute ${isRTL ? 'right-0 rounded-l-md' : 'left-0 rounded-r-md'} w-1 h-8 bg-primary-600 dark:bg-primary-400`}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
            </Link>
          );
        }

        const itemKey = `item-${index}`;
        const isItemExpanded = expandedKeys.has(itemKey);

        return (
          <Accordion
            key={`accordion-${index}`}
            selectedKeys={expandedKeys}
            onSelectionChange={(keys) => {
              console.log('🔄 Accordion Selection Changed:', {
                itemTitle: item.title,
                itemIndex: index,
                newKeys: Array.from(keys as Set<string>),
                previousKeys: Array.from(expandedKeys),
                itemIsActive
              });
              setExpandedKeys(keys as Set<string>);
            }}
            selectionMode="multiple"
            disableIndicatorAnimation
          >
            <AccordionItem
              key={itemKey}
              aria-label={item.title}
              indicator={({ isOpen }) => {
                // console.log('🎯 Indicator:', { itemTitle: item.title, isOpen, isItemExpanded });
                return (
                  <div
                    className="transition-transform duration-200 ease-in-out"
                    style={{ 
                      transform: isOpen ? 'rotate(0deg)' : (isRTL ? 'rotate(90deg)' : 'rotate(-90deg)')
                    }}
                  >
                    <Icon 
                      icon="lucide:chevron-down"
                      width={16} 
                      className={itemIsActive 
                        ? "text-primary-600 dark:text-primary-400" 
                        : "text-gray-500 dark:text-gray-400"
                      }
                    />
                  </div>
                );
              }}
              title={
                <div className="flex items-center gap-3 w-full">
                  <Icon icon={item.icon} width={20} className="text-gray-700 dark:text-gray-300 shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 flex-1">{item.title}</span>
                </div>
              }
              classNames={{
                title: "py-2 text-sm font-normal w-full",
                trigger: "px-2 py-0 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all-fast",
                content: "py-0 mt-1"
              }}
            >
              <div className={`space-y-1 ${isRTL ? 'pe-8' : 'ps-8'}`}>
                {item.items?.map((subItem) => {
                  const subItemIsActive = isActive(subItem.url, false);
                  
                  return (
                    <Link
                      key={subItem.title}
                      to={subItem.url}
                      onClick={() => handleLinkClick(subItem.url)}
                      className={`flex items-center gap-3 py-2 px-3 ${DESIGN_TOKENS.navItem} transition-all-fast text-sm relative
                        ${subItemIsActive
                          ? 'bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400 font-medium'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        }
                      `}
                    >
                      <Icon icon={subItem.icon} width={16} className="shrink-0" />
                      <span>{subItem.title}</span>
                    </Link>
                  );
                })}
              </div>
            </AccordionItem>
          </Accordion>
        );
      })}
    </div>
  );
};